import { DateTime } from 'luxon';
import { AnalyticsByDay, Period } from 'graphql/types';
export const colorSet = new Map<string, string>([
  ['Meeting', '#2196f3'],
  ['Scheduling', '#e02e74'],
  ['Messaging', '#4caf50'],
  ['Email', '#ffeb3b'],
  ['Software Development', '#a60acc'],
  ['Project Management', '#230acc'],
  ['Design', '#0accb7'],
  ['IT Service Management', '#cbcc0a'],
  ['Browser', '#c66f08']
]);

const defaultColor = '#607d8b';

export type dataType = {
  name: string;
  color: string;
  data: AnalyticsByDay[];
};

export type UtilizationDataType = {
  categoryName: string;
  data: { date: string; totalCount: number; totalDuration: number }[];
};
export function getDaterange(
  currentPeriod: Period | null,
  checked: boolean,
  data: UtilizationDataType[]
): Set<string> {
  let dateSet = new Set<string>();
  if (!checked) {
    let startDate = DateTime.fromISO(currentPeriod?.startDate);
    const endDate = DateTime.fromISO(currentPeriod?.endDate);

    while (startDate <= endDate) {
      dateSet.add(startDate.toISODate());
      startDate = startDate.plus({ days: 1 });
    }
  } else {
    let dates: string[] = [];
    data.forEach((v) => {
      dates = [...dates, ...v.data.map((x) => x.date)];
    });
    dateSet = new Set(dates);
  }
  return dateSet;
}

export function transformData<T>(data: T[]): UtilizationDataType[] {
  const existingCategoryObj: Record<string, number> = {};
  return data?.reduce((acc: UtilizationDataType[], item: any) => {
    const name: string = item?.dimension?.name;
    if (name) {
      if (name in existingCategoryObj) {
        const idx = existingCategoryObj[name];
        acc[idx].data.push({
          date: item.dimension.metadata[0].value,
          totalCount: item.metrics.totalCount,
          totalDuration: item.metrics.totalDuration
        });
      } else {
        const v = {
          categoryName: item.dimension.name,
          data: [
            {
              date: item.dimension.metadata[0].value,
              totalCount: item.metrics.totalCount,
              totalDuration: item.metrics.totalDuration
            }
          ]
        };
        acc.push(v);
        existingCategoryObj[name] = acc.length - 1;
      }
    }

    return acc;
  }, []);
}

export function transformUtilzationAnalyticsData(
  data: UtilizationDataType[],
  checked: boolean,
  currentPeriod: Period | null
): dataType[] {
  let dateSet: Set<string>;
  dateSet = getDaterange(currentPeriod, checked, data);

  const transformedData = data.map((d) => {
    if (checked) {
      dateSet = getDaterange(currentPeriod, checked, data);
    }
    const color = colorSet.get(d.categoryName) || defaultColor;

    const existingData = [...d.data];
    const presentDates = d.data.map((x) => x.date);
    dateSet.forEach((z) => {
      if (!presentDates.includes(z)) {
        existingData.push({
          date: z,
          totalCount: 0,
          totalDuration: 0
        });
      }
    });
    existingData.sort((a, b) => {
      if (a.date < b.date) {
        return -1;
      }
      if (a.date > b.date) {
        return 1;
      }
      return 0;
    });

    return {
      name: d.categoryName,
      color: color,
      data: existingData
    };
  });

  transformedData.sort((a, b) => a.name.localeCompare(b.name));

  return transformedData;
}

export function transformIntoUtilizationChartData<T>(
  data: T[] | undefined,
  checked: boolean,
  currentPeriod: Period | null
): dataType[] {
  const d = transformData(data || []);
  const transformD = transformUtilzationAnalyticsData(
    d,
    checked,
    currentPeriod
  );
  return transformD;
}
